<script lang="ts">
  /**
   * One question, as an excerpt of what the member actually wrote, with the question
   * itself marked inside it.
   *
   * DELIBERATELY NOT `<SkoolPost>` / `<SkoolComment>`. Those render a piece of Skool
   * content properly and completely — title, media, upvotes, comment count, footer —
   * which is right on the timeline and in the moderation deck, where the content IS the
   * subject. Here it is EVIDENCE for a theme, and a list of ten complete cards buries
   * the theme it is evidence for. Three earlier attempts all failed the same way: the
   * full thread (4,300px a card), then the thread with the ask above it, then the ask as
   * a full card — each one still asked the reader to find the question inside a piece of
   * content rendered at full weight.
   *
   * THE HIGHLIGHT IS THE WHOLE POINT. The detector found ONE SENTENCE; the body around
   * it is context. Marking it means an owner reads the question, not a paragraph that
   * contains one — and on a comment under a win post, that is the difference between
   * seeing "$499 closed!" and seeing "what types of groups?".
   *
   * Clicking opens the thread, focused on this row. So the card owes the reader an
   * excerpt and nothing else: no upvotes, no comment count, no media. Those are one
   * click away, rendered by the components that do it properly.
   */
  import Avatar from '@ui/Avatar.svelte';
  import MemberHover from '@ui/MemberHover.svelte';
  import { skoolToPlain } from '@ui/skoolContent';
  import type { DigestQuote } from '@api';

  let { quote, onopen }: { quote: DigestQuote; onopen: () => void } = $props();

  /** How much of the body to keep around the question. Enough to see who they are
   *  talking to and what about; short enough that ten of these stay a list. */
  const PAD = 150;

  /**
   * The body split into `[before, question, after]`, or `[body]` when the sentence
   * cannot be located.
   *
   * The detector strips markdown and mentions before matching, so its sentence is
   * frequently NOT a substring of the raw body. Falling back to the plain excerpt is
   * correct in that case: a wrong highlight is worse than none, because it would mark a
   * sentence nobody asked.
   */
  const parts = $derived.by((): [string, string, string] | [string] => {
    // Plain text, both sides. Skool bodies carry mention and list markup
    // (`[@Name](obj://user/…)`, `[li]`) which would otherwise show as source in an
    // excerpt — and the detector already strips mentions from its sentence, so
    // stripping the body is also what makes the two align well enough to match.
    const body = skoolToPlain(quote.content ?? quote.text ?? '');
    const q = skoolToPlain(quote.text ?? '');
    if (!body) return [q];
    if (!q) return [body];
    const at = body.indexOf(q);
    if (at === -1) {
      // Try the tail of the sentence — mentions are stripped from the front of a
      // reply ("@Maren Saracki, do you have tips…") far more often than the end.
      const tail = q.length > 40 ? q.slice(-40) : null;
      const t = tail ? body.indexOf(tail) : -1;
      if (t === -1) return [body];
      return [body.slice(0, t), body.slice(t, t + tail!.length), body.slice(t + tail!.length)];
    }
    return [body.slice(0, at), q, body.slice(at + q.length)];
  });

  const before = $derived(
    parts.length === 3 ? clampStart(parts[0] as string) : clampStart(parts[0] as string),
  );
  const marked = $derived(parts.length === 3 ? (parts[1] as string) : null);
  const after = $derived(parts.length === 3 ? clampEnd(parts[2] as string) : '');

  /** Keep the tail of what came before — the words nearest the question. */
  function clampStart(s: string): string {
    const t = s.trim();
    return t.length <= PAD ? t : '…' + t.slice(-PAD).replace(/^\S*\s/, '');
  }
  function clampEnd(s: string): string {
    const t = s.trim();
    return t.length <= PAD ? t : t.slice(0, PAD).replace(/\s\S*$/, '') + '…';
  }

  function ago(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = (Date.now() - new Date(iso).getTime()) / 86_400_000;
    if (d < 1) return 'today';
    if (d < 7) return `${Math.round(d)}d ago`;
    if (d < 60) return `${Math.round(d / 7)}w ago`;
    return `${Math.round(d / 30)}mo ago`;
  }
</script>

<!-- A DIV WRAPPING A BUTTON, not one button around everything. `<MemberHover>` renders
     a popover containing a link, and a link inside a button is invalid markup that
     browsers resolve by dropping one of them. So the header is static and the excerpt is
     the click target — which is also the honest hit area: the avatar's job is the hover
     card, the excerpt's job is opening the thread. `<AdminMembers>` solves the same
     collision the same way, with a clickable row rather than a button. -->
<div class="rounded-box border hair bg-base-100 px-3 py-2.5">
  <div class="flex items-center gap-2">
    <MemberHover memberId={quote.author?.member_id ?? 0} class="shrink-0">
      <!-- `memberId` is what puts the contributor-role ring and its corner badge on the
           face; without it this is just a picture. -->
      <Avatar
        src={quote.author?.picture_profile ?? null}
        name={quote.author?.name ?? ''}
        memberId={quote.author?.member_id ?? null}
        size={26}
      />
    </MemberHover>
    <span class="redact truncate text-[0.78rem] font-medium">{quote.author?.name ?? 'Unknown'}</span>
    <span class="eyebrow shrink-0 text-[0.55rem] text-base-content/40">{ago(quote.asked_at)}</span>
    {#if quote.source === 'comment' && quote.parent_title}
      <!-- Named, not rendered. The post a question hangs under is context — and on the
           30% of comment-questions that sit beneath a win post, rendering it is what
           made the digest look like it was reporting celebrations. -->
      <span class="redact eyebrow ml-auto hidden min-w-0 truncate text-[0.55rem] text-base-content/35 sm:block">
        in · {quote.parent_title}
      </span>
    {:else if quote.title}
      <span class="redact eyebrow ml-auto hidden min-w-0 truncate text-[0.55rem] text-base-content/35 sm:block">
        {quote.title}
      </span>
    {/if}
  </div>

  <button
    class="mt-1.5 block w-full rounded text-left transition-colors hover:bg-base-200/50"
    onclick={onopen}
    title="Open the thread"
  >
    <p class="redact text-[0.84rem] leading-relaxed text-base-content/60">
      {before}{#if marked}<mark
          class="rounded bg-primary/15 px-0.5 text-base-content decoration-clone">{marked}</mark
        >{/if}{after}
    </p>
  </button>
</div>
